import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Country, Location } from "../types";

export type LoadingStatus =
  | "idle"
  | "loading"
  | "ready"
  | "needs-input"
  | "error";

export type UserMetaData = {
  clientIp: string | null;
  clientCountry: Country | null;
  countryResolutionStatus: LoadingStatus;
  clientAvailableLocations: Location[];
  locationsCountry: string | null;
  locationsStatus: LoadingStatus;
  availableJobCategories: string[];
  availableEmploymentTypes: string[];
  facetsCountry: string | null;
  facetsStatus: LoadingStatus;
};

const initialState: UserMetaData = {
  clientIp: null,
  clientCountry: null,
  countryResolutionStatus: "idle",
  clientAvailableLocations: [],
  locationsCountry: null,
  locationsStatus: "idle",
  availableJobCategories: [],
  availableEmploymentTypes: [],
  facetsCountry: null,
  facetsStatus: "idle",
};

export const userMetaDataSlice = createSlice({
  name: "userMetaData",
  initialState,
  reducers: {
    setClientIp(state, action: PayloadAction<string>) {
      state.clientIp = action.payload;
    },
    resetClientIp(state) {
      state.clientIp = null;
    },
    startCountryResolution(state) {
      state.countryResolutionStatus = "loading";
    },
    requireCountrySelection(state) {
      state.clientCountry = null;
      state.countryResolutionStatus = "needs-input";
      resetCountryMetadata(state);
    },
    setClientCountry(state, action: PayloadAction<Country | null>) {
      const countryChanged =
        state.clientCountry?.countryName !== action.payload?.countryName;
      state.clientCountry = action.payload;
      state.countryResolutionStatus = "ready";

      if (countryChanged) resetCountryMetadata(state);
    },
    resetClientCountry(state) {
      state.clientCountry = null;
      state.countryResolutionStatus = "idle";
      resetCountryMetadata(state);
    },
    resetUserMetaDataSlice() {
      return initialState;
    },
    setClientAvailableLocations(state, action: PayloadAction<Location[]>) {
      state.clientAvailableLocations = action.payload;
      state.locationsCountry = state.clientCountry?.countryName ?? null;
      state.locationsStatus = "ready";
    },
    resetClientAvailableLocations(state) {
      state.clientAvailableLocations = [];
      state.locationsCountry = null;
      state.locationsStatus = "idle";
    },
    setAvailableJobCategories(state, action: PayloadAction<string[]>) {
      state.availableJobCategories = action.payload;
    },
    resetAvailableJobCategories(state) {
      state.availableJobCategories = [];
    },
    setAvailableEmploymentTypes(state, action: PayloadAction<string[]>) {
      state.availableEmploymentTypes = action.payload;
    },
    resetAvailableEmploymentTypes(state) {
      state.availableEmploymentTypes = [];
    },
    startLocationsLoading(state, action: PayloadAction<string>) {
      state.locationsCountry = action.payload;
      state.locationsStatus = "loading";
    },
    finishLocationsLoading(
      state,
      action: PayloadAction<{ country: string; locations: Location[] }>,
    ) {
      if (state.clientCountry?.countryName !== action.payload.country) return;
      state.clientAvailableLocations = action.payload.locations;
      state.locationsCountry = action.payload.country;
      state.locationsStatus = "ready";
    },
    failLocationsLoading(state, action: PayloadAction<string>) {
      if (state.clientCountry?.countryName !== action.payload) return;
      state.locationsStatus = "error";
    },
    startFacetsLoading(state, action: PayloadAction<string>) {
      state.facetsCountry = action.payload;
      state.facetsStatus = "loading";
    },
    finishFacetsLoading(
      state,
      action: PayloadAction<{
        country: string;
        categories: string[];
        employmentTypes: string[];
      }>,
    ) {
      if (state.clientCountry?.countryName !== action.payload.country) return;
      state.availableJobCategories = action.payload.categories;
      state.availableEmploymentTypes = action.payload.employmentTypes;
      state.facetsCountry = action.payload.country;
      state.facetsStatus = "ready";
    },
    failFacetsLoading(state, action: PayloadAction<string>) {
      if (state.clientCountry?.countryName !== action.payload) return;
      state.facetsStatus = "error";
    },
  },
});

function resetCountryMetadata(state: UserMetaData) {
  state.clientAvailableLocations = [];
  state.locationsCountry = null;
  state.locationsStatus = "idle";
  state.availableJobCategories = [];
  state.availableEmploymentTypes = [];
  state.facetsCountry = null;
  state.facetsStatus = "idle";
}

export const {
  setClientIp,
  resetClientIp,
  startCountryResolution,
  requireCountrySelection,
  setClientCountry,
  resetClientCountry,
  resetUserMetaDataSlice,
  setClientAvailableLocations,
  resetClientAvailableLocations,
  setAvailableJobCategories,
  resetAvailableJobCategories,
  setAvailableEmploymentTypes,
  resetAvailableEmploymentTypes,
  startLocationsLoading,
  finishLocationsLoading,
  failLocationsLoading,
  startFacetsLoading,
  finishFacetsLoading,
  failFacetsLoading,
} = userMetaDataSlice.actions;

export default userMetaDataSlice.reducer;
